/**
 * The saved corrections the sitting screens did not have (D-174): one try at a lesson, a past
 * challenge, and what the screen of a sitting adds (back to the history, « Refaire », the trainer's
 * report, the exercises of a placement on the course).
 */

import { MarkNotes } from "./MarkNotes";
import { useAcademyAttemptsReviewRetrieve, useAcademyHistoryAttemptsRetrieve, useAcademyHistoryChallengesRetrieve, useAcademyHistoryLessonsRetrieve } from "@agencyos/api-client";
import type { HistoryItem } from "@agencyos/api-client";
import { type LessonProductionView, historyTitle, parseChallengeReview, parseLessonProduction } from "@agencyos/core";
import { Badge, GlassCard, Skeleton } from "@agencyos/ui-web";
import { ArrowLeft, CheckCircle2, Hourglass, Lightbulb, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { ChallengeReview } from "./ChallengeReview";
import "./history.css";
import { AudioReplay } from "./ListeningReplay";
import { RecordingExpired } from "./RecordingExpired";
import { RetakeButton } from "./RetakeButton";
import { dateText } from "./shared";
import { isObject, parseGrade } from "./types";
import { PageHead, SectionTitle } from "./ui";

function BackToHistory() {
  const { t } = useTranslation();
  return (
    <Link to="/academy/history" className="ac-back">
      <ArrowLeft aria-hidden size={16} />
      {t("academyHistory.back")}
    </Link>
  );
}

function Production({ production, speak }: { production: LessonProductionView; speak: boolean }) {
  const { t } = useTranslation();
  const grade = parseGrade(production.grade);
  return (
    <div className="ac-block">
      <h3 className="ac-label">{t("academyHistory.lesson.yourWork")}</h3>
      {production.audioUrl && <AudioReplay url={production.audioUrl} label={t("academy.results.listenAgain")} />}
      {production.audioExpired && <RecordingExpired />}
      {production.text && (
        <details>
          <summary>{speak ? t("academyHistory.lesson.transcript") : t("academyHistory.lesson.text")}</summary>
          <p className="ac-production-text">{production.text}</p>
          {!speak && <p className="ac-footnote ao-num">{t("academyHistory.lesson.words", { count: production.wordCount })}</p>}
        </details>
      )}
      {production.status === "submitted" && (
        <p className="ac-status ac-secondary">
          <Hourglass aria-hidden size={18} /> {t("academyHistory.lesson.waiting")}
        </p>
      )}
      {production.status === "to_trainer" && <p className="ac-status ac-secondary">{t("academyHistory.lesson.toTrainer")}</p>}
      <MarkNotes grade={production.grade} />
      {production.score !== null && <p className="ao-num">{t("academyHistory.lesson.score", { score: production.score })}</p>}
      {production.band !== null && production.score === null && <p className="ao-num">{t("academyHistory.lesson.band", { band: production.band })}</p>}
      {grade.criteria.length > 0 && (
        <ul className="ac-checklist">
          {grade.criteria.map((c) => (
            <li key={c.key}>{c.comment || c.key}</li>
          ))}
        </ul>
      )}
      {grade.comment && <p className="ac-comment">{grade.comment}</p>}
      {grade.errors.length > 0 && (
        <ul className="ac-errors">
          {grade.errors.map((e, index) => (
            <li key={index} className="ac-error-row">
              <p>
                <del>{e.excerpt}</del> → <ins>{e.correction}</ins>
              </p>
              {e.explanation && <p className="ac-footnote">{e.explanation}</p>}
            </li>
          ))}
        </ul>
      )}
      {grade.corrected && (
        <details>
          <summary>{t("academy.results.corrected")}</summary>
          <p className="ac-production-text">{grade.corrected}</p>
        </details>
      )}
      {grade.model && (
        <details>
          <summary>{t("academy.results.model")}</summary>
          <p className="ac-production-text">{grade.model}</p>
        </details>
      )}
      {grade.advice.length > 0 && (
        <div className="ac-block ac-advice">
          <h4 className="ac-label">
            <Lightbulb aria-hidden size={14} /> {t("academy.results.advice")}
          </h4>
          <ul>
            {grade.advice.map((a, index) => (
              <li key={index}>{a}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Header({ item, eyebrow }: { item: HistoryItem; eyebrow: string }) {
  const { i18n, t } = useTranslation();
  return (
    <PageHead
      eyebrow={eyebrow}
      title={historyTitle(item.title, i18n.language) || t(`academyHistory.type.${item.type}`)}
      subtitle={<p className="ao-num">{dateText(item.at, i18n.language)}</p>}
      aside={item.percent !== null ? <p className="ac-head-stat__value ao-num">{item.percent} %</p> : undefined}
    />
  );
}

/** /academy/history/lesson/:id — the correction of one try at a lesson. */
export function HistoryLessonPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const [search] = useSearchParams();
  const exam = search.get("exam");
  const query = useAcademyHistoryLessonsRetrieve(id, exam ? { exam: exam as never } : undefined, { query: { retry: false } });
  const data = query.data?.status === 200 ? query.data.data : undefined;
  if (query.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (!data) {
    return (
      <div className="ac-page">
        <BackToHistory />
        <p className="ac-secondary">{t("academy.results.notFound")}</p>
      </div>
    );
  }
  const raw = Array.isArray(data.exercises) ? data.exercises.filter(isObject) : [];
  return (
    <div className="ac-page ac-results">
      <BackToHistory />
      <Header item={data.item} eyebrow={data.item.source === "lesson_try" ? t("academyHistory.lesson.past") : t("academyHistory.lesson.eyebrow")} />
      <GlassCard className="ac-card">
        <RetakeButton item={data.item} />
      </GlassCard>
      <section className="ac-section" aria-labelledby="history-exercises">
        <SectionTitle id="history-exercises">{t("academyHistory.lesson.exercises")}</SectionTitle>
        {!raw.length && <p className="ac-secondary">{t("academyHistory.lesson.notAnswered")}</p>}
        <ol className="ac-review-list">
          {raw.map((row) => {
            const production = parseLessonProduction(row.production);
            const review = production ? null : parseChallengeReview([row])[0];
            const correct = review?.correct === true;
            return (
              <li key={String(row.id)} className={`ac-review ac-surface${production ? "" : correct ? " ac-review--right" : " ac-review--wrong"}`}>
                {!production && (
                  <p className="ac-review__head">
                    <span className="ac-review__mark" aria-hidden="true">
                      {correct ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                    </span>
                    <span className="visually-hidden">{correct ? t("academy.player.right") : t("academy.results.wrongShort")}</span>
                  </p>
                )}
                <p className="ac-review__stem">{String(row.prompt ?? "")}</p>
                {review && (
                  <dl className="ac-review__answers">
                    <div>
                      <dt className="ac-caption">{t("listening.review.given")}</dt>
                      <dd>{review.answered ? review.given : t("academy.results.noAnswer")}</dd>
                    </div>
                    <div>
                      <dt className="ac-caption">{t("listening.review.right")}</dt>
                      <dd>{review.right}</dd>
                    </div>
                  </dl>
                )}
                {production && <Production production={production} speak={row.type === "speak"} />}
                {typeof row.explanation === "string" && row.explanation && <p className="ac-review__why">{row.explanation}</p>}
              </li>
            );
          })}
        </ol>
      </section>
    </div>
  );
}

/** /academy/history/challenge/:id — a past challenge's correction. */
export function HistoryChallengePage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const query = useAcademyHistoryChallengesRetrieve(id, { query: { retry: false } });
  const data = query.data?.status === 200 ? query.data.data : undefined;
  if (query.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (!data) {
    return (
      <div className="ac-page">
        <BackToHistory />
        <p className="ac-secondary">{t("academy.results.notFound")}</p>
      </div>
    );
  }
  const language = data.item.exam === "ielts_general" ? "en" : data.item.exam === "goethe" ? "de" : "fr";
  return (
    <div className="ac-page ac-results">
      <BackToHistory />
      <Header item={data.item} eyebrow={t("academyHistory.challenge.eyebrow")} />
      <ChallengeReview rows={parseChallengeReview(data.review)} lang={language} />
    </div>
  );
}

/** Under the results of a sitting: back to the history, « Refaire », the trainer's report, and the
 * exercises of a placement on the course. Nothing for a sitting under way. */
export function AttemptHistoryPanel({ attempt }: { attempt: string }) {
  const { t } = useTranslation();
  const query = useAcademyHistoryAttemptsRetrieve(attempt, { query: { retry: false } });
  const item = query.data?.status === 200 ? query.data.data : undefined;
  const open = !item || item.status === "in_progress";
  const review = useAcademyAttemptsReviewRetrieve(attempt, { query: { retry: false, enabled: !open && item?.type === "placement" } });
  const reviewData: unknown = review.data?.status === 200 ? review.data.data : undefined;
  const rows = isObject(reviewData) ? parseChallengeReview(reviewData.exercises) : [];
  if (open || !item) return null;
  const language = item.exam === "ielts_general" ? "en" : item.exam === "goethe" ? "de" : "fr";
  return (
    <div className="ac-page ac-history__panel">
      {rows.length > 0 && <ChallengeReview rows={rows} lang={language} />}
      {item.trainer_report && (
        <GlassCard as="section" className="ac-card" aria-labelledby="history-report">
          <h2 id="history-report" className="ac-card-title">
            {t("academyHistory.trainerReport")}
          </h2>
          <p className="ac-production-text">{item.trainer_report}</p>
        </GlassCard>
      )}
      <GlassCard className="ac-card ac-history__panel-actions">
        <RetakeButton item={item} />
        <Link className="ac-more" to="/academy/history">
          {t("academyHistory.toHistory")}
        </Link>
        <Badge tone={item.status === "graded" ? "success" : "warning"}>{t(`academyHistory.status.${item.status}`)}</Badge>
      </GlassCard>
    </div>
  );
}
